import { BorderlessDropdown } from '@klarna/bubble-ui';
import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';

import { formUtils } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { PAYMENT_INTERVAL } from '@/constants';
import { IntegerInput } from '@/components';

import { CY_UNDERWRITING_INFO_CONTAINER_IDS, PAYMENT_DELAY_LIMITS } from './constants';

export function prepareFields(props, tPrefix) {
  const { t, paymentIntervals, defaultState = {} } = props;
  const {
    yearlyAcquiredVolume,
    paymentDelay,
    paymentInterval = PAYMENT_INTERVAL.DAILY,
  } = defaultState;

  const yearlyAcquiredVolumeField = {
    component: IntegerInput,
    size: 1,
    label: t(`${tPrefix}.fields.yearly_acquired_volume.label`),
    value: _isNumber(yearlyAcquiredVolume) ? String(yearlyAcquiredVolume) : '',
    isInitialized: false,
    isValid: false,
    validators: [
      {
        callback: validators.common.isBigInteger({ min: 1 }),
        errorMessage: t(`${tPrefix}.fields.yearly_acquired_volume.errors.invalid`),
      },
      {
        callback: validators.common.isString({ min: 1, max: 16 }),
        errorMessage: t(`${tPrefix}.fields.yearly_acquired_volume.errors.too_long`),
      },
    ],
    extraProps: {
      // we don't cast to number because we may get BigInt from prefill data
      // thus we are validating by the length of string
      castToNumber: false,
      unsigned: true,
      maxNumOfDigits: 16,
      useGrouping: true,
      ...generateCyElemId(CY_UNDERWRITING_INFO_CONTAINER_IDS.INPUT_YEARLY_ACQUIRED_VOLUME, window),
    },
  };

  const paymentDelayField = {
    component: IntegerInput,
    size: 1,
    label: t(`${tPrefix}.fields.payment_delay.label`),
    value: _isNumber(paymentDelay) ? String(paymentDelay) : '',
    isInitialized: false,
    isValid: false,
    validators: [
      {
        callback: validators.common.isRequired,
        errorMessage: t(`${tPrefix}.fields.payment_delay.errors.general`),
      },
      {
        callback: (val, { paymentInterval }) => {
          const limits = PAYMENT_DELAY_LIMITS[paymentInterval] || {};
          return validators.common.isInteger(limits)(val);
        },
        errorMessage: ({ paymentInterval }) => {
          const limits = PAYMENT_DELAY_LIMITS[paymentInterval];
          return t(
            `${tPrefix}.fields.payment_delay.errors.${limits ? 'invalid' : 'general'}`,
            limits,
          );
        },
        stateSelector: state => ({
          paymentInterval: _get(state, 'fields.paymentInterval.value'),
        }),
      },
    ],
    extraProps: {
      castToNumber: false,
      ...generateCyElemId(CY_UNDERWRITING_INFO_CONTAINER_IDS.PAYMENT_DELAY, window),
    },
  };

  const paymentIntervalField = {
    component: BorderlessDropdown,
    size: 1,
    label: t(`${tPrefix}.fields.payment_interval.label`),
    value: paymentInterval,
    isInitialized: false,
    isValid: false,
    validators: [
      {
        callback: validators.common.isRequired,
        errorMessage: t(`${tPrefix}.fields.payment_interval.errors.invalid`),
      },
    ],
    extraProps: {
      options: paymentIntervals,
      ...generateCyElemId(CY_UNDERWRITING_INFO_CONTAINER_IDS.PAYMENT_INTERVAL, window),
    },
  };

  const fields = formUtils.initForm({
    yearlyAcquiredVolume: yearlyAcquiredVolumeField,
    paymentInterval: paymentIntervalField,
    paymentDelay: paymentDelayField,
  });

  return fields;
}

export function getFieldValues(fields) {
  const values = {
    yearlyAcquiredVolume: Number(fields.yearlyAcquiredVolume.value),
    paymentDelay: Number(fields.paymentDelay.value),
    paymentInterval: fields.paymentInterval.value,
  };

  return values;
}
